<template>
  <div
    :class="[
      $style.taskBar,
      {
        [$style.active]: isTaskActive(task),
        [$style.completed]: task.completed,
        [$style.completing]: isCompleting,
        [$style.swiping]: isSwiping || swipeDistance > 0,
      },
    ]"
    :style="{
      '--bar-color': adjustedColor,
      '--estimate': getEstimateWidth(),
    }"
    :data-view-mode="viewMode"
    :data-swipe-open="swipeDistance > 0"
    @click="handleTaskAreaClick"
    ref="taskBarRef"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
    @touchstart.passive="false"
  >
    <!-- Swipe Actions Container (positioned behind content) - Touch devices only -->
    <SwipeActions
      v-if="isTouchDevice"
      :task="task"
      :is-swiping="isSwiping"
      :swipe-distance="swipeDistance"
      :max-swipe-distance="MAX_SWIPE_DISTANCE"
      @complete="handleSwipeActionClick('complete')"
      @edit="handleSwipeActionClick('edit')"
      @delete="handleSwipeActionClick('delete')"
    />

    <!-- Main Content Container (slides left to reveal actions on touch devices) -->
    <div
      :class="$style.mainContent"
      :style="isTouchDevice ? mainContentStyle : {}"
    >
      <div :class="$style.sessionSegments">
        <div
          v-for="(session, index) in task.sessions"
          :key="index"
          :class="$style.sessionSegment"
          :style="getTimelinePosition(session)"
        ></div>
      </div>
      <div :class="$style.taskInfo" :style="{ color: textColor }">
        <div :class="$style.taskNameContainer">
          <TaskName :task="task">
            <ProjectIcon
              v-if="task.projectId"
              :size="16"
              :class="$style.projectIcon"
            />
          </TaskName>
        </div>
        <div :class="$style.timeInfo">
          <TimeDisplay
            :duration="currentDuration"
            :is-active="isTaskActive(task)"
            label=""
            @click="handleToggle"
          />
        </div>
        <!-- Traditional Action Buttons - Desktop/larger screens only -->
        <ActionButtons
          v-if="!isTouchDevice"
          :task="task"
          @complete="handleToggleCompleted"
          @edit="showDetailsDialog = true"
          @delete="$emit('delete')"
        />
        <!-- Completion indicator - only visible on touch devices where swipe actions are used -->
        <div v-if="isTouchDevice" :class="$style.completionIndicatorContainer">
          <div v-if="task.completed" :class="$style.completionIndicator">
            <CheckCircleIcon :size="20" />
          </div>
        </div>
      </div>
    </div>
    <TaskDetailsDialog
      :is-open="showDetailsDialog"
      :task="task"
      @close="showDetailsDialog = false"
      @update:topic="updateTopicFromDialog"
      @update:project="updateProjectFromDialog"
      @update:estimatedDuration="updateEstimatedDurationFromDialog"
      @update:lastSessionDuration="updateLastSessionDurationFromDialog"
      @update:sessionStartTime="updateSessionStartTimeFromDialog"
      @update:sessionEndTime="updateSessionEndTimeFromDialog"
      @update:overrunReasons="updateOverrunReasonsFromDialog"
      @update:name="$emit('update:name', $event)"
      @delete-session="deleteSessionFromDialog"
      @move-sessions="handleMoveSessions"
    />
  </div>

  <!-- Guide popover for new tasks -->
  <GuidePopover
    :is-visible="showStartGuide"
    :text="startGuideText"
    :target-element="taskBarRef || undefined"
    position="bottom"
    :z-index="100"
    @close="closeStartGuide"
    @dismiss="dismissStartGuide"
  />
</template>

<script setup lang="ts">
import type { Task, WorkSession } from "../types/task";
import TaskName from "./TaskName.vue";
import TimeDisplay from "./TimeDisplay.vue";
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { adjustColorSaturation } from "../utils/colorUtils";
import TaskDetailsDialog from "./TaskDetailsDialog.vue";
import GuidePopover from "./GuidePopover.vue";
import { guideService } from "../services/guideService";
import { TimePeriod, ViewMode } from "../types/enums";
import SwipeActions from "./SwipeActions.vue";
import ActionButtons from "./ActionButtons.vue";
import { CheckCircleIcon, ProjectIcon } from "./icons";
import { isTaskActive } from "../services/taskService";

const props = defineProps<{
  task: Task;
  workdayMs: number;
  isNewTask?: boolean;
  viewMode: ViewMode;
  filterPeriod: string;
  dateRange: { start: string; end: string };
  saturation: number;
}>();

console.log("TaskBar received task:", props.task);
console.log("Task duration:", props.task.duration);

const showDetailsDialog = ref(false);
const isCompleting = ref(false);
const taskBarRef = ref<HTMLElement | null>(null);
const timer = ref<number | null>(null);

// Swipe action state
const touchStartX = ref(0);
const touchStartY = ref(0);
const currentTouchX = ref(0);
const isSwiping = ref(false);
const swipeDistance = ref(0);
const SWIPE_THRESHOLD = 40; // Minimum distance to trigger swipe
const MAX_SWIPE_DISTANCE = 180; // Maximum swipe distance (3 buttons × 60px)

const adjustedColor = computed(() => {
  if (props.saturation === 100) {
    return props.task.color;
  }
  return adjustColorSaturation(props.task.color, props.saturation);
});

// Detect touch device
const isTouchDevice = computed(() => {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
});

// Main content style (slides left to reveal actions)
const mainContentStyle = computed(() => {
  if (!isSwiping.value && swipeDistance.value === 0) {
    return { transform: "translateX(0)" };
  }

  const translateX = Math.min(swipeDistance.value, MAX_SWIPE_DISTANCE);
  return { transform: `translateX(-${translateX}px)` };
});

// Calculate current duration for active tasks
const currentDuration = computed(() => {
  // Access timer.value to make this computed reactive
  if (timer.value) {
    // This forces the computed to re-evaluate when timer changes
  }

  if (!isTaskActive(props.task)) {
    return props.task.duration;
  }

  // For active tasks, calculate duration including the current session
  let totalDuration = props.task.duration;

  // Add duration of the current active session
  if (props.task.sessions && props.task.sessions.length > 0) {
    const lastSession = props.task.sessions[props.task.sessions.length - 1];
    if (!lastSession.endTime) {
      // Session is still active, add time from start to now
      const sessionStart = new Date(lastSession.startTime);
      const now = new Date();
      totalDuration += now.getTime() - sessionStart.getTime();
    }
  }

  return totalDuration;
});

// Calculate text color for good contrast with background
const textColor = computed(() => {
  const color = props.task.color;
  if (!color) return "white";

  // Extract RGB values from HSL
  const hslMatch = color.match(/hsl\(([^,]+),\s*([^,]+)%,\s*([^,]+)%\)/);
  if (hslMatch) {
    const h = parseFloat(hslMatch[1]);
    const s = parseFloat(hslMatch[2]);
    const l = parseFloat(hslMatch[3]);

    // Convert HSL to RGB for brightness calculation
    const c = ((1 - Math.abs((2 * l) / 100 - 1)) * s) / 100;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l / 100 - c / 2;

    let r, g, b;
    if (h < 60) {
      r = c;
      g = x;
      b = 0;
    } else if (h < 120) {
      r = x;
      g = c;
      b = 0;
    } else if (h < 180) {
      r = 0;
      g = c;
      b = x;
    } else if (h < 240) {
      r = 0;
      g = x;
      b = c;
    } else if (h < 300) {
      r = x;
      g = 0;
      b = c;
    } else {
      r = c;
      g = 0;
      b = x;
    }

    const brightness = (r + g + b) * 255 + m * 255;
    return brightness > 128 ? "#000000" : "#ffffff";
  }

  return "white"; // fallback
});

const emit = defineEmits<{
  (e: "toggle"): void;
  (e: "delete"): void;
  (e: "update:name", name: string): void;
  (e: "update:duration", duration: number): void;
  (e: "update:estimate", duration: number): void;
  (e: "update:topic", topic: string): void;
  (e: "update:project", project?: string): void;
  (e: "toggle:completed"): void;
  (e: "update:sessions", sessions: WorkSession[]): void;
  (e: "update:task", task: Task): void;
  (
    e: "move-sessions",
    data: {
      sessionIndices: number[];
      newTaskName: string;
      newTaskTopicId: string;
      newTaskEstimatedDuration: number;
    }
  ): void;
}>();

const showStartGuide = ref(false);

// Responsive guide text
const startGuideText = computed(() => {
  const isTouchDevice =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;

  if (isTouchDevice || isSmallScreen) {
    return "Tap to start";
  }
  return "Click to start";
});

const GUIDE_ID = "start-task-guide";

// Start timer for active tasks
onMounted(() => {
  if (props.isNewTask && guideService.shouldShowGuide(GUIDE_ID)) {
    setTimeout(() => {
      showStartGuide.value = true;
    }, 500); // Small delay to ensure the task is rendered
  }

  // Start timer if task is active
  if (isTaskActive(props.task)) {
    startTimer();
  }

  // Add window focus listener to update timer immediately when refocused
  window.addEventListener("focus", handleWindowFocus);
});

// Clean up timer
onUnmounted(() => {
  if (timer.value) {
    clearInterval(timer.value);
    timer.value = null;
  }

  // Remove window focus listener
  window.removeEventListener("focus", handleWindowFocus);
});

// Watch for changes in task active state
watch(
  () => isTaskActive(props.task),
  (isActive) => {
    if (isActive) {
      startTimer();
    } else {
      if (timer.value) {
        clearInterval(timer.value);
        timer.value = null;
      }
    }
  }
);

function handleWindowFocus() {
  // Immediately update timer when window is refocused
  if (isTaskActive(props.task) && timer.value) {
    // Force reactivity - the computed currentDuration will recalculate
    // This is a no-op but ensures reactivity
  }
}

function startTimer() {
  if (timer.value) {
    clearInterval(timer.value);
  }
  timer.value = setInterval(() => {
    // Force reactivity - the computed currentDuration will recalculate
    // This is a no-op but ensures the interval runs
  }, 1000);
}

function closeStartGuide() {
  showStartGuide.value = false;
}

function dismissStartGuide() {
  guideService.dismissGuide(GUIDE_ID);
  showStartGuide.value = false;
}

// Clear the guide when task is started
function handleToggle() {
  console.log(
    "handleToggle called for task:",
    props.task.name,
    "ID:",
    props.task.id,
    "isActive:",
    isTaskActive(props.task)
  );

  if (showStartGuide.value) {
    showStartGuide.value = false;
  }

  // Prevent starting timer on completed tasks
  // But allow uncompleting by starting
  if (props.task.completed && !isTaskActive(props.task)) {
    console.log("Task is completed, preventing toggle");
    return;
  }

  // Close swipe actions if they're open
  if (swipeDistance.value > 0) {
    resetSwipe();
    return;
  }

  console.log("Emitting toggle event for task ID:", props.task.id);
  emit("toggle");
  resetSwipe(); // Reset swipe state when task is toggled
}

// Handle clicks on the main task area to close swipe actions
function handleTaskAreaClick(event: Event) {
  // If swipe actions are open, close them instead of toggling
  if (swipeDistance.value > 0) {
    resetSwipe();
    event.stopPropagation();
    return;
  }

  // Otherwise, proceed with normal toggle behavior
  handleToggle();
}

function getEstimateWidth(): string {
  if (props.task.estimatedDuration > 0) {
    // For tasks with estimates, the striped area represents the estimated portion of total duration
    const totalDuration = currentDuration.value; // Use currentDuration for real-time updates
    const estimatedDuration = props.task.estimatedDuration;

    // Calculate what percentage of the total duration the estimate represents
    const percentage =
      totalDuration > 0 ? (estimatedDuration / totalDuration) * 100 : 100;
    return `${percentage}%`;
  } else {
    // For tasks without estimates, use workday duration as baseline
    const percentage = Math.min((props.workdayMs / props.workdayMs) * 100, 100);
    return `${percentage}%`;
  }
}

function handleToggleCompleted() {
  if (!props.task.completed) {
    // Starting completion animation
    isCompleting.value = true;
    setTimeout(() => {
      emit("toggle:completed");
      isCompleting.value = false;
    }, 800); // Animation duration
  } else {
    // Uncompleting - no animation needed
    emit("toggle:completed");
  }

  // Close swipe actions after use
  resetSwipe();
}

function handleSwipeActionClick(action: string) {
  console.log(`TaskBar: Received swipe action: "${action}"`);

  switch (action) {
    case "complete":
      console.log("TaskBar: Handling complete action");
      handleToggleCompleted();
      break;
    case "edit":
      console.log("TaskBar: Handling edit action - opening details dialog");
      showDetailsDialog.value = true;
      resetSwipe();
      break;
    case "delete":
      console.log("TaskBar: Handling delete action");
      emit("delete");
      resetSwipe();
      break;
    default:
      console.warn(`TaskBar: Unknown action: "${action}"`);
  }
}

function updateTopicFromDialog(topic: string) {
  emit("update:topic", topic);
}

function updateProjectFromDialog(project?: string) {
  emit("update:project", project);
}

function updateEstimatedDurationFromDialog(duration: number) {
  emit("update:estimate", duration);
}

function updateLastSessionDurationFromDialog(duration: number) {
  // Update the last session's end time based on the new duration
  const lastSession =
    props.task.sessions && props.task.sessions.length > 0
      ? props.task.sessions[props.task.sessions.length - 1]
      : null;

  if (!lastSession) return;

  const startTime = new Date(lastSession.startTime);
  const newEndTime = new Date(startTime.getTime() + duration);

  const updatedSessions = [...props.task.sessions];
  updatedSessions[updatedSessions.length - 1] = {
    ...lastSession,
    endTime: newEndTime.toISOString(),
  };

  emit("update:sessions", updatedSessions);
}

function updateOverrunReasonsFromDialog(reasons: string[]) {
  // Update the task's overrun reasons
  const updatedTask = { ...props.task, overrunReasons: reasons };
  emit("update:task", updatedTask);
}

function deleteSessionFromDialog(sessionIndex: number) {
  if (!Array.isArray(props.task.sessions)) return;
  const newSessions = props.task.sessions.slice();
  newSessions.splice(sessionIndex, 1);
  // Recalculate duration from sessions
  const newDuration = newSessions.reduce((total, session) => {
    if (!session.endTime) return total;
    return (
      total +
      (new Date(session.endTime).getTime() -
        new Date(session.startTime).getTime())
    );
  }, 0);
  // Emit updated sessions to parent
  emitUpdateSessions(newSessions, newDuration);
}

function handleMoveSessions(data: {
  sessionIndices: number[];
  newTaskName: string;
  newTaskTopicId: string;
  newTaskEstimatedDuration: number;
}) {
  emit("move-sessions", data);
}

function updateSessionStartTimeFromDialog(
  sessionIndex: number,
  startTime: string
) {
  console.log("TaskBar: updateSessionStartTimeFromDialog called with:", {
    sessionIndex,
    startTime,
  });
  // Update the session's start time
  const session =
    props.task.sessions && props.task.sessions.length > sessionIndex
      ? props.task.sessions[sessionIndex]
      : null;

  if (!session) {
    console.log("TaskBar: session not found for index:", sessionIndex);
    return;
  }

  const updatedSessions = [...props.task.sessions];
  updatedSessions[sessionIndex] = {
    ...session,
    startTime: startTime,
  };

  console.log("TaskBar: updatedSessions:", updatedSessions);

  // Recalculate duration from sessions
  const newDuration = updatedSessions.reduce((total, session) => {
    if (!session.endTime) return total;
    return (
      total +
      (new Date(session.endTime).getTime() -
        new Date(session.startTime).getTime())
    );
  }, 0);

  console.log("TaskBar: newDuration:", newDuration);
  console.log("TaskBar: emitting update:sessions and update:duration");
  emitUpdateSessions(updatedSessions, newDuration);
}

function updateSessionEndTimeFromDialog(sessionIndex: number, endTime: string) {
  console.log("TaskBar: updateSessionEndTimeFromDialog called with:", {
    sessionIndex,
    endTime,
  });
  // Update the session's end time
  const session =
    props.task.sessions && props.task.sessions.length > sessionIndex
      ? props.task.sessions[sessionIndex]
      : null;

  if (!session) {
    console.log("TaskBar: session not found for index:", sessionIndex);
    return;
  }

  const updatedSessions = [...props.task.sessions];
  updatedSessions[sessionIndex] = {
    ...session,
    endTime: endTime,
  };

  console.log("TaskBar: updatedSessions:", updatedSessions);

  // Recalculate duration from sessions
  const newDuration = updatedSessions.reduce((total, session) => {
    if (!session.endTime) return total;
    return (
      total +
      (new Date(session.endTime).getTime() -
        new Date(session.startTime).getTime())
    );
  }, 0);

  console.log("TaskBar: newDuration:", newDuration);
  console.log("TaskBar: emitting update:sessions and update:duration");
  emitUpdateSessions(updatedSessions, newDuration);
}

function emitUpdateSessions(sessions: WorkSession[], duration: number) {
  console.log("TaskBar: emitUpdateSessions called with:", {
    sessions,
    duration,
  });
  emit("update:sessions", sessions);
  emit("update:duration", duration);
}

// Touch gesture handlers
function handleTouchStart(event: TouchEvent) {
  const touch = event.touches[0];
  touchStartX.value = touch.clientX;
  touchStartY.value = touch.clientY;
  currentTouchX.value = touch.clientX;
  isSwiping.value = false;
  swipeDistance.value = 0;
}

function handleTouchMove(event: TouchEvent) {
  const touch = event.touches[0];
  const deltaX = touch.clientX - touchStartX.value;
  const deltaY = touch.clientY - touchStartY.value;

  // Only allow horizontal swiping
  if (Math.abs(deltaX) < Math.abs(deltaY)) {
    return;
  }

  // Start swiping after threshold
  if (Math.abs(deltaX) > 5) {
    isSwiping.value = true;
  }

  if (isSwiping.value) {
    // Prevent default scrolling only when actively swiping
    event.preventDefault();

    // Swipe left (negative deltaX) to reveal actions
    if (deltaX < 0) {
      // Swipe left to open actions (negative deltaX becomes positive distance)
      // Add some resistance for more natural feel
      const resistance = 0.8;
      swipeDistance.value = Math.min(
        Math.abs(deltaX) * resistance,
        MAX_SWIPE_DISTANCE
      );
    } else if (deltaX > 0 && swipeDistance.value > 0) {
      // Swipe right to close actions
      swipeDistance.value = Math.max(0, swipeDistance.value - deltaX);
    }
    currentTouchX.value = touch.clientX;
  }
}

function handleTouchEnd() {
  if (!isSwiping.value) {
    return;
  }

  // Check if swipe threshold was met
  if (swipeDistance.value >= SWIPE_THRESHOLD) {
    // Keep the swipe open
    isSwiping.value = false;
  } else {
    // Reset swipe position
    swipeDistance.value = 0;
    isSwiping.value = false;
  }
}

// Reset swipe when task changes
watch(
  () => props.task.id,
  () => {
    swipeDistance.value = 0;
    isSwiping.value = false;
  }
);

// Reset swipe state
function resetSwipe() {
  swipeDistance.value = 0;
  isSwiping.value = false;
}

// Timeline calculations
const timelineRange = computed(() => {
  const now = new Date();
  let startTime: Date;
  let endTime: Date;

  switch (props.filterPeriod) {
    case TimePeriod.ALL: {
      // For "all" period, show today's totals as a reasonable default
      const today = new Date();
      startTime = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );
      endTime = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        23,
        59,
        59,
        999
      );
      break;
    }
    case TimePeriod.TODAY: {
      const today = new Date();
      startTime = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );
      endTime = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        23,
        59,
        59,
        999
      );
      break;
    }
    case TimePeriod.WEEK: {
      const startOfWeek = new Date();
      const dayOfWeek = startOfWeek.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

      // Calculate days to subtract to get to Monday (1)
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startTime = new Date(startOfWeek);
      startTime.setDate(startOfWeek.getDate() - daysToSubtract);
      startTime.setHours(0, 0, 0, 0);

      endTime = new Date(startTime);
      endTime.setDate(startTime.getDate() + 6);
      endTime.setHours(23, 59, 59, 999);
      break;
    }
    case TimePeriod.MONTH: {
      const today = new Date();
      startTime = new Date(today.getFullYear(), today.getMonth(), 1);
      startTime.setHours(0, 0, 0, 0);

      endTime = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      endTime.setHours(23, 59, 59, 999);
      break;
    }
    case TimePeriod.YEAR: {
      const today = new Date();
      startTime = new Date(today.getFullYear(), 0, 1);
      endTime = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        23,
        59,
        59,
        999
      );
      break;
    }
    case TimePeriod.CUSTOM: {
      if (props.dateRange.start && props.dateRange.end) {
        startTime = new Date(props.dateRange.start);
        endTime = new Date(props.dateRange.end);
        endTime.setHours(23, 59, 59, 999);
      } else {
        startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endTime = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          23,
          59,
          59,
          999
        );
      }
      break;
    }
    default: {
      startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      endTime = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
        999
      );
    }
  }

  return { startTime, endTime };
});

const timelineDuration = computed(() => {
  return (
    timelineRange.value.endTime.getTime() -
    timelineRange.value.startTime.getTime()
  );
});

function getTimelinePosition(session: WorkSession): {
  left: string;
  width: string;
} {
  if (props.viewMode === ViewMode.TIMELINE) {
    const sessionStart = new Date(session.startTime);
    const sessionEnd = session.endTime ? new Date(session.endTime) : new Date();

    // Use the earlier of current time or filter end time for active sessions
    const effectiveEnd = session.endTime
      ? sessionEnd
      : new Date(
          Math.min(sessionEnd.getTime(), timelineRange.value.endTime.getTime())
        );

    const left =
      ((sessionStart.getTime() - timelineRange.value.startTime.getTime()) /
        timelineDuration.value) *
      100;
    const width =
      ((effectiveEnd.getTime() - sessionStart.getTime()) /
        timelineDuration.value) *
      100;

    // Ensure minimum width for visibility
    const minWidth = 2; // 2% minimum width
    const adjustedWidth = Math.max(width, minWidth);

    return {
      left: `${Math.max(0, left)}%`,
      width: `${adjustedWidth}%`,
    };
  } else {
    // Compressed view: calculate proportional width based on session duration
    const sessionDuration = session.endTime
      ? new Date(session.endTime).getTime() -
        new Date(session.startTime).getTime()
      : Date.now() - new Date(session.startTime).getTime();

    // Use estimated duration as the baseline if available, otherwise use total actual duration
    const baselineDuration =
      props.task.estimatedDuration > 0
        ? props.task.estimatedDuration
        : props.task.sessions.reduce((total, s) => {
            const duration = s.endTime
              ? new Date(s.endTime).getTime() - new Date(s.startTime).getTime()
              : Date.now() - new Date(session.startTime).getTime();
            return total + duration;
          }, 0);

    const widthPercentage =
      baselineDuration > 0 ? (sessionDuration / baselineDuration) * 100 : 0;

    // Calculate left position based on previous segments
    const sessionIndex = props.task.sessions.indexOf(session);
    let leftPercentage = 0;

    for (let i = 0; i < sessionIndex; i++) {
      const prevSession = props.task.sessions[i];
      const prevDuration = prevSession.endTime
        ? new Date(prevSession.endTime).getTime() -
          new Date(prevSession.startTime).getTime()
        : Date.now() - new Date(prevSession.startTime).getTime();
      leftPercentage += (prevDuration / baselineDuration) * 100;
    }

    // No gaps in compressed view - sessions are continuous
    return {
      left: `${leftPercentage}%`,
      width: `${widthPercentage}%`,
    };
  }
}
</script>

<style module>
.taskBar {
  width: 100%;
  height: 60px;
  margin: 10px 0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  opacity: 0.8;
  background: rgba(255, 255, 255, 0.1) !important;
  overflow: hidden;
  /* Prevent touch selection and improve swipe experience */
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

/* Main Content Container */
.mainContent {
  position: relative;
  z-index: 2; /* Above the swipe actions */
  background: inherit;
  width: 100%;
  height: 100%;
  transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
  backface-visibility: hidden;
}

/* Overrun area background - now moves with content */
.mainContent::after {
  content: "";
  position: absolute;
  top: 0;
  left: var(--estimate);
  width: calc(100% - var(--estimate));
  height: 100%;
  background: rgba(128, 128, 128, 0.5);
  pointer-events: none;
  z-index: 1; /* Behind other content but moves with container */
}

/* Visual feedback during swiping */
.taskBar.swiping {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.taskBar[data-swipe-open="true"] {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

.taskBar::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: var(--estimate);
  height: 100%;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  pointer-events: none;
}

/* Hide dark background in timeline view */
.taskBar[data-view-mode="timeline"]::before {
  display: none;
}

/* Add session segments */
.taskBar .sessionSegments {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: stretch;
  pointer-events: none;
}

.taskBar .sessionSegment {
  background-color: var(--bar-color) !important;
  border-radius: 4px;
  margin-right: 2px;
  transition: all 0.3s ease;
  opacity: 0.8;
}

.taskBar .sessionSegment:last-child {
  margin-right: 0;
}

/* Compressed view: use absolute positioning for proportional widths */
.taskBar:not([data-view-mode="timeline"]) .sessionSegments {
  display: block;
}

.taskBar:not([data-view-mode="timeline"]) .sessionSegment {
  position: absolute;
  top: 0;
  bottom: 0;
  margin-right: 0;
  border-radius: 0;
  /* border-right: 2px solid rgba(255, 255, 255, 0.1); */
}

.taskBar:not([data-view-mode="timeline"]) .sessionSegment:last-child {
  border-right: none;
}

/* Timeline view specific styles */
.taskBar[data-view-mode="timeline"] .sessionSegments {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: block;
}

.taskBar[data-view-mode="timeline"] .sessionSegment {
  position: absolute;
  top: 0;
  bottom: 0;
  margin-right: 0 !important;
  border-radius: 2px;
  background-color: var(--bar-color) !important;
  border: 1px solid var(--bar-color);
  opacity: 0.9;
}

.taskBar:hover {
  opacity: 1;
}

/* Only apply transform on non-touch devices */
@media (hover: hover) and (pointer: fine) {
  .taskBar:hover {
    transform: translateX(5px);
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
  }
  50% {
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
  }
  100% {
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
  }
}

@keyframes complete {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  25% {
    transform: scale(1.05);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  75% {
    transform: scale(1.05);
    opacity: 0.6;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.4;
  }
}

@keyframes checkmark {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.taskBar.active {
  opacity: 1;
  border: 2px solid rgba(255, 255, 255, 0.3);
  transform: scale(1.01);
  animation: pulse 2s ease-in-out infinite;
}

.taskBar.completing {
  animation: complete 0.8s ease-in-out forwards;
  pointer-events: none;
}

.taskBar.completing .completeButton svg {
  animation: checkmark 0.6s ease-out 0.2s both;
}

.taskBar.active .taskInfo {
  color: inherit;
}

.taskBar.active .taskName,
.taskBar.active .taskTopic,
.taskBar.active .taskTime,
.taskBar.active .taskEstimated {
  color: inherit;
}

.taskBar.active .taskTopic {
  opacity: 0.9;
}

.taskBar.active .taskTime {
  font-weight: 600;
}

.taskBar.active .taskEstimated {
  opacity: 0.9;
}

.taskBar.active .taskDurationInput {
  background: rgba(255, 255, 255, 0.2);
}

.taskInfo {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  width: calc(100% - 30px);
  z-index: 1;
}

.taskNameContainer {
  flex: 1;
  min-width: 0; /* Allow shrinking */
  text-align: left;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-start;
}

.projectIcon {
  flex-shrink: 0;
  opacity: 0.8;
  color: var(--text-primary);
}

.timeInfo {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0; /* Prevent shrinking */
  margin-right: 200px;
}

.taskActions {
  display: flex;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2;
  flex-shrink: 0; /* Prevent shrinking */
}

.taskBar:hover .taskActions {
  opacity: 1;
}

@media (max-width: 1024px) {
  .taskActions {
    opacity: 1 !important;
  }
}

.actionButton {
  background: none;
  border: none;
  min-width: 24px;
  min-height: 24px;
  padding: 0;
  border-radius: 4px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Only apply hover effects on devices that support hover */
@media (hover: hover) and (pointer: fine) {
  .actionButton:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }

  .completeButton:hover {
    background: rgba(0, 184, 148, 0.2);
    color: #00b894;
  }

  .detailsButton:hover {
    background: rgba(116, 185, 255, 0.2);
    color: var(--task-meeting);
  }

  .deleteButton:hover {
    background: rgba(255, 118, 117, 0.2);
    color: var(--task-design);
  }
}

.completeButton.completed {
  color: #00b894;
}

.taskBar.completed {
  opacity: 0.6;
}

.taskBar.completed:hover {
  opacity: 0.8;
}

.taskBar.completed .taskName {
  text-decoration: line-through;
}

.taskBar.completed .timeInfo {
  opacity: 0.8;
}

/* Completion indicator - only visible on small screens where action buttons are hidden */

.completionIndicatorContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0; /* Prevent shrinking */
}
.completionIndicator {
  display: flex;
  align-items: center;
  color: #00b894;
  opacity: 0.9;
  background: rgba(0, 184, 148, 0.1);
  border-radius: 50%;
  padding: 4px;
}

@keyframes completionPulse {
  0%,
  100% {
    transform: translateY(-50%) scale(1);
    opacity: 0.9;
  }
  50% {
    transform: translateY(-50%) scale(1.1);
    opacity: 1;
  }
}

/* Responsive adjustments for smaller screens */
@media (max-width: 768px) {
  .taskInfo {
    left: 10px;
    gap: 8px;
    width: calc(100% - 20px);
  }

  .timeInfo {
    margin-right: 200px;
    gap: 1px;
  }

  .taskActions {
    gap: 4px;
  }

  .actionButton {
    min-width: 36px;
    min-height: 36px;
    padding: 0;
  }

  /* Swipe actions responsive adjustments */
  .swipeAction {
    min-width: 5px;
  }
}

@media (max-width: 480px) {
  .taskInfo {
    left: 8px;
    gap: 6px;
    width: calc(100% - 16px);
  }

  .timeInfo {
    margin-right: 10px;
  }

  .taskActions {
    gap: 3px;
  }

  /* Swipe actions responsive adjustments */
  .swipeAction {
    min-width: 5px;
  }
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  .swipeActions {
    transform: translateX(100%);
  }

  .taskBar[data-swipe-open="true"] .swipeActions {
    transform: translateX(0);
  }
}
</style>
